import type { ApiErrorBody } from "@pushup/types";

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/**
 * Builds a readable one-line message from a structured validation error body
 * (e.g. "name: required; imageDataUrl: too large") so callers see the actual
 * reason instead of just the generic status text.
 */
function formatValidationMessage(details: unknown): string | undefined {
  const fieldErrors = (details as { fieldErrors?: Record<string, string[]> }).fieldErrors;
  if (!fieldErrors) {
    return undefined;
  }
  const parts = Object.entries(fieldErrors)
    .filter(([, errors]) => errors.length > 0)
    .map(([field, errors]) => `${field}: ${errors.join(", ")}`);
  return parts.length > 0 ? parts.join("; ") : undefined;
}

export interface ApiClientOptions {
  baseUrl: string;
  fetch?: typeof globalThis.fetch;
}

export interface ApiClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  delete<T>(path: string, init?: RequestInit): Promise<T>;
  request<T>(path: string, init?: RequestInit): Promise<T>;
}

/**
 * Minimal typed REST client used by the frontend. Throws `ApiClientError`
 * with the backend's structured error body on non-2xx responses.
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const { baseUrl } = options;

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const fetchImpl = options.fetch ?? globalThis.fetch;
    let fullUrl = `${baseUrl}${path}`;
    if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
      const g = globalThis as { window?: { location?: { origin?: string } } };
      const origin =
        typeof g.window !== "undefined" &&
        g.window.location?.origin &&
        g.window.location.origin !== "null"
          ? g.window.location.origin
          : "http://localhost:5173";
      fullUrl = `${origin}${fullUrl.startsWith("/") ? "" : "/"}${fullUrl}`;
    }

    const res = await fetchImpl(fullUrl, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...init.headers,
      },
    });

    if (!res.ok) {
      let body: ApiErrorBody | undefined;
      try {
        body = (await res.json()) as ApiErrorBody;
      } catch {
        // Non-JSON error body (e.g. proxy error page).
      }
      const validationMessage =
        body?.error.code === "VALIDATION_ERROR"
          ? formatValidationMessage(body?.error.details)
          : undefined;
      throw new ApiClientError(
        res.status,
        body?.error.code ?? "UNKNOWN",
        validationMessage ?? body?.error.message ?? `Request failed with status ${res.status}`,
        body?.error.details,
      );
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }

  return {
    get: <T>(path: string, init?: RequestInit) => request<T>(path, { ...init, method: "GET" }),
    post: <T>(path: string, body?: unknown, init?: RequestInit) =>
      request<T>(path, {
        ...init,
        method: "POST",
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    put: <T>(path: string, body?: unknown, init?: RequestInit) =>
      request<T>(path, {
        ...init,
        method: "PUT",
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    delete: <T>(path: string, init?: RequestInit) =>
      request<T>(path, { ...init, method: "DELETE" }),
    request,
  };
}
